import axios from 'axios';
import { encryptLoginPayload } from '@/utils/crypto';

const isDev = process.env.NODE_ENV === 'development';
const localUrl = 'https://api.dev.mvpick.net/api';
export const API_BASE_URL = isDev ? localUrl : (process.env.REACT_APP_API_BASE_URL || '/api');

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// 관리자/학원/학생 토큰 자동 첨부 + 만료 임박 시 선제 갱신
api.interceptors.request.use(config => {
    const url = config.url || '';
    // 경로 세그먼트 기반 컨텍스트 판별 (slug 지원)
    const pathname = (typeof window !== 'undefined') ? (window.location?.pathname || '') : '';
    const segments = pathname.split('/').filter(Boolean); // ['', 'slug', 'academy', ...] -> ['slug','academy',...]
    const first = segments[0] || '';
    const second = segments[1] || '';
    const isAdminContext = first === 'admin';
    const isAcademyContext = first === 'academy' || second === 'academy';
    const isStudentContext = !isAdminContext && !isAcademyContext;
    let token = null;
    // 관리자 화면에서는 어떤 엔드포인트이든 admin 토큰을 우선 사용
    if (isAdminContext) {
        token = localStorage.getItem('admin_token') || localStorage.getItem('academy_token');
    } else if (url.startsWith('/admin/')) {
        // 비관리자 화면이어도 /admin/** 호출이면 admin 토큰
        token = localStorage.getItem('admin_token');
    } else if (url.startsWith('/student/') || isStudentContext) {
        // 학생 엔드포인트 호출이거나 학생 화면에서는 student 토큰 사용
        token = localStorage.getItem('student_token');
    } else {
        // 그 외(academy 등)는 academy_token 우선, 없으면 admin_token 사용
        token = localStorage.getItem('academy_token') || localStorage.getItem('admin_token');
    }
    if (token) {
        // 만료 임박(<=60초) 시 리프레시 시도. 토큰 페이로드 파싱(단순 base64) 실패 시 무시
        try {
            const payload = JSON.parse(atob(token.split('.')[1] || ''));
            const expSec = payload?.exp ? Number(payload.exp) : 0;
            const nowSec = Math.floor(Date.now() / 1000);
            if (expSec && expSec - nowSec <= 60) {
                const isAdmin = url.startsWith('/admin/') || isAdminContext;
                const isStudent = url.startsWith('/student/') || isStudentContext;
                let refreshToken = null;
                let refreshPath = null;
                
                if (isAdmin) {
                    refreshToken = localStorage.getItem('admin_refresh_token');
                    refreshPath = '/admin/auth/refresh';
                } else if (isStudent) {
                    refreshToken = localStorage.getItem('student_refresh_token');
                    refreshPath = '/student/auth/refresh';
                } else {
                    refreshToken = localStorage.getItem('academy_refresh_token');
                    refreshPath = '/auth/refresh';
                }
                
                if (refreshToken) {
                    // 동기 요청 방지: 별도 axios 인스턴스로 호출
                    const refreshClient = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' } });
                    return refreshClient.post(refreshPath, { refreshToken })
                        .then(res => {
                            const newToken = res?.data?.data?.token || res?.data?.token;
                            if (newToken) {
                                if (isAdmin) localStorage.setItem('admin_token', newToken);
                                else if (isStudent) localStorage.setItem('student_token', newToken);
                                else localStorage.setItem('academy_token', newToken);
                                config.headers.Authorization = `Bearer ${newToken}`;
                            } else {
                                config.headers.Authorization = `Bearer ${token}`;
                            }
                            return config;
                        })
                        .catch(() => {
                            config.headers.Authorization = `Bearer ${token}`;
                            return config;
                        });
                }
            }
        } catch (_) {}
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// 401/403 전역 처리
api.interceptors.response.use(
    (res) => res,
    async (error) => {
        const status = error?.response?.status;
        const reqUrl = error?.config?.url || '';
        const skipAuthRedirect = reqUrl.startsWith('/session/slug');
        const clearAdminAuthState = () => {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_refresh_token');
        };
        const clearAcademyAuthState = () => {
            localStorage.removeItem('academy_token');
            localStorage.removeItem('academy_refresh_token');
            localStorage.removeItem('academy_role');
        };
        const clearStudentAuthState = () => {
            localStorage.removeItem('student_token');
            localStorage.removeItem('student_refresh_token');
        };
        const logoutStudentAndRedirect = () => {
            clearStudentAuthState();
            localStorage.removeItem('student_user_id');
            localStorage.removeItem('student_user_name');
            localStorage.removeItem('student_display_name');
            localStorage.removeItem('student_email');
            localStorage.removeItem('student_academy_name');
            localStorage.removeItem('student_grade_label');
            localStorage.removeItem('student_birth_date');
            const segments = (typeof window !== 'undefined') ? (window.location?.pathname || '').split('/').filter(Boolean) : [];
            const first = segments[0] || '';
            const isSlugContext = first && first !== 'admin' && first !== 'academy' && first !== 'student' && first !== 'login' && first !== 'signup' && first !== 'report';
            window.location.href = isSlugContext ? `/${first}/login` : '/login';
        };
        const message = error?.response?.data?.message || '';
        // 경로 세그먼트 기반 컨텍스트 판별 (slug 지원)
        const pathname = (typeof window !== 'undefined') ? (window.location?.pathname || '') : '';
        const segments = pathname.split('/').filter(Boolean);
        const first = segments[0] || '';
        const second = segments[1] || '';
        const isAdminContext = first === 'admin';
        const isAcademyContext = first === 'academy' || second === 'academy';
        const isStudentContext = !isAdminContext && !isAcademyContext;
        const isStudentRequest = reqUrl.startsWith('/student/') || isStudentContext;
        const normalizedMessage = (message || '').toLowerCase();

        if (skipAuthRedirect && (status === 401 || status === 403)) {
            if (isAdminContext || reqUrl.startsWith('/admin/')) {
                clearAdminAuthState();
            } else if (isStudentContext || reqUrl.startsWith('/student/')) {
                clearStudentAuthState();
            } else {
                clearAcademyAuthState();
            }
            return Promise.reject(error);
        }

        if (status === 403 && isStudentRequest) {
            if (message.includes('퇴원') || normalizedMessage.includes('withdraw')) {
                logoutStudentAndRedirect();
                return Promise.reject(error);
            }
        }

        // 리프레시 토큰 만료 메시지 체크
        const isRefreshTokenError = message.includes('리프레시 토큰') || 
                                   message.includes('refresh token') || 
                                   normalizedMessage.includes('expired') || 
                                   normalizedMessage.includes('invalid');

        // 인증 관련 요청 및 결제 관련 요청은 리다이렉트 루프 방지 위해 제외
        const isAuthFlow = reqUrl.startsWith('/admin/auth') || reqUrl.startsWith('/auth') || reqUrl.startsWith('/student/auth') || reqUrl.startsWith('/academy/registration');
        const isPaymentFlow = reqUrl.includes('/payment') || reqUrl.includes('/papers/confirm-payment') || reqUrl.includes('/papers/webhook');

        // 리프레시 토큰 만료 메시지가 있으면 즉시 로그아웃 처리 (단, 결제 플로우는 제외)
        if (isRefreshTokenError && !isAuthFlow && !isPaymentFlow) {
            if (reqUrl.startsWith('/admin/') || isAdminContext) {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('admin_refresh_token');
                window.location.href = '/admin/login';
                return Promise.reject(error);
            } else if (isStudentRequest) {
                logoutStudentAndRedirect();
                return Promise.reject(error);
            } else {
                localStorage.removeItem('academy_token');
                localStorage.removeItem('academy_refresh_token');
                localStorage.removeItem('academy_role');
                // slug 기반 접근 시에도 /academy/login 으로 유도 (로그인 후 slug 컨텍스트 유지 필요 시 추후 개선)
                window.location.href = '/academy/login';
                return Promise.reject(error);
            }
        }

        if (!isAuthFlow && !isPaymentFlow) {
            // 401 처리
            if (status === 401) {
                // 401 시 한 번 리프레시 토큰으로 재시도
                try {
                    const isAdmin = reqUrl.startsWith('/admin/') || isAdminContext;
                    const isStudent = isStudentRequest;
                    let refreshToken = null;
                    let refreshPath = null;
                    
                    if (isAdmin) {
                        refreshToken = localStorage.getItem('admin_refresh_token');
                        refreshPath = '/admin/auth/refresh';
                    } else if (isStudent) {
                        refreshToken = localStorage.getItem('student_refresh_token');
                        refreshPath = '/student/auth/refresh';
                    } else {
                        refreshToken = localStorage.getItem('academy_refresh_token');
                        refreshPath = '/auth/refresh';
                    }
                    
                    if (refreshToken) {
                        const res = await axios.post(API_BASE_URL + refreshPath, { refreshToken }, { headers: { 'Content-Type': 'application/json' } });
                        const newToken = res?.data?.data?.token || res?.data?.token;
                        if (newToken) {
                            if (isAdmin) localStorage.setItem('admin_token', newToken);
                            else if (isStudent) localStorage.setItem('student_token', newToken);
                            else localStorage.setItem('academy_token', newToken);
                            // 원요청 재시도
                            error.config.headers.Authorization = `Bearer ${newToken}`;
                            return api.request(error.config);
                        }
                    }
                } catch (refreshError) {
                    // 리프레시 실패 시 로그아웃 처리
                    console.log('Refresh token failed, logging out...');
                }
                
                // 리프레시 실패 또는 리프레시 토큰 없음 - 로그아웃 처리
                if (reqUrl.startsWith('/admin/') || isAdminContext) {
                    localStorage.removeItem('admin_token');
                    localStorage.removeItem('admin_refresh_token');
                    window.location.href = '/admin/login';
                } else if (isStudentRequest) {
                    logoutStudentAndRedirect();
                } else if (reqUrl.startsWith('/academy/')) {
                    localStorage.removeItem('academy_token');
                    localStorage.removeItem('academy_refresh_token');
                    localStorage.removeItem('academy_role');
                    window.location.href = '/academy/login';
                }
            }

            // 403 처리: Access Denied
            if (status === 403) {
                // 토큰 만료 여부 체크 - 만료되었으면 로그아웃
                const isAdmin = reqUrl.startsWith('/admin/') || isAdminContext;
                const isStudent = isStudentRequest;
                
                // 토큰이 만료되었는지 확인하는 헬퍼 함수
                const isTokenExpired = (token) => {
                    if (!token) return true;
                    try {
                        const payload = JSON.parse(atob(token.split('.')[1] || ''));
                        const expSec = payload?.exp ? Number(payload.exp) : 0;
                        const nowSec = Math.floor(Date.now() / 1000);
                        return expSec <= nowSec;
                    } catch {
                        return true; // 파싱 실패 시 만료로 간주
                    }
                };
                
                if (isAdmin) {
                    const adminToken = localStorage.getItem('admin_token');
                    // 토큰이 없거나 만료되었으면 로그아웃
                    if (!adminToken || isTokenExpired(adminToken)) {
                        localStorage.removeItem('admin_token');
                        localStorage.removeItem('admin_refresh_token');
                        window.location.href = '/admin/login';
                        return Promise.reject(error);
                    }
                } else if (isStudent) {
                    const studentToken = localStorage.getItem('student_token');
                    // 토큰이 없거나 만료되었으면 로그아웃
                    if (!studentToken || isTokenExpired(studentToken)) {
                        logoutStudentAndRedirect();
                        return Promise.reject(error);
                    }
                } else {
                    const academyToken = localStorage.getItem('academy_token');
                    // 토큰이 없거나 만료되었으면 로그아웃
                    if (!academyToken || isTokenExpired(academyToken)) {
                        localStorage.removeItem('academy_token');
                        localStorage.removeItem('academy_refresh_token');
                        localStorage.removeItem('academy_role');
                        window.location.href = '/academy/login';
                        return Promise.reject(error);
                    }
                }
                
                // 토큰이 있고 유효한데 403이면 권한 부족 (로그인 유지)
                return Promise.reject(error);
            }
        } else {
            // 인증 플로우에서 실패한 경우 (리프레시 토큰 자체가 만료된 경우)
            // 이미 위에서 isRefreshTokenError로 처리되었으므로,
            // 401이면 로그아웃 처리
            if (status === 401 || status === 403) {
                // [QA Fix] 로그인 API 호출 시 발생한 401/403은 리다이렉트(새로고침) 하지 않고 컴포넌트로 에러 전달
                if (reqUrl.endsWith('/login')) {
                    return Promise.reject(error);
                }

                const isAdmin = reqUrl.startsWith('/admin/auth');
                const isStudent = reqUrl.startsWith('/student/auth');
                const isAcademy = reqUrl.startsWith('/auth');
                
                if (isStudent) {
                    logoutStudentAndRedirect();
                } else if (isAdmin) {
                    localStorage.removeItem('admin_token');
                    localStorage.removeItem('admin_refresh_token');
                    if (window.location.pathname !== '/admin/login') {
                        window.location.href = '/admin/login';
                    }
                } else if (isAcademy) {
                    localStorage.removeItem('academy_token');
                    localStorage.removeItem('academy_refresh_token');
                    localStorage.removeItem('academy_role');
                    if (window.location.pathname !== '/academy/login') {
                        window.location.href = '/academy/login';
                    }
                }
            }
        }
        return Promise.reject(error);
    }
);

export const sampleApi = {
    // 전체 샘플 데이터 조회
    getAllSamples: async () => {
        try {
            const response = await api.get('/sample');
            return response.data;
        } catch (error) {
            console.error('Error fetching samples:', error);
            throw error;
        }
    },

    // 단일 샘플 데이터 조회
    getSampleById: async (id) => {
        try {
            const response = await api.get(`/sample/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching sample ${id}:`, error);
            throw error;
        }
    },

    // 샘플 데이터 생성
    createSample: async (data) => {
        try {
            const response = await api.post('/sample', data);
            return response.data;
        } catch (error) {
            console.error('Error creating sample:', error);
            throw error;
        }
    },

    // 샘플 데이터 수정
    updateSample: async (id, data) => {
        try {
            const response = await api.put(`/sample/${id}`, data);
            return response.data;
        } catch (error) {
            console.error(`Error updating sample ${id}:`, error);
            throw error;
        }
    },

    // 샘플 데이터 삭제
    deleteSample: async (id) => {
        try {
            const response = await api.delete(`/sample/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error deleting sample ${id}:`, error);
            throw error;
        }
    },

    // 헬스 체크
    healthCheck: async () => {
        try {
            const response = await api.get('/sample/health');
            return response.data;
        } catch (error) {
            console.error('Error checking health:', error);
            throw error;
        }
    }
};

export const adminAuthApi = {
    login: async ({ loginId, password }) => {
        const { encryptedLoginId, encryptedPassword } = await encryptLoginPayload(loginId, password);
        const res = await api.post('/admin/auth/login', { encryptedLoginId, encryptedPassword });
        return res.data;
    },
    verify: async ({ loginId, code }) => {
        const res = await api.post('/admin/auth/verify', { loginId, code });
        return res.data;
    },
};

// 관리자 - 계정 관리 API
export const adminAccountApi = {
    /**
     * 관리자 비밀번호 변경
     * @param {{ currentPassword: string, newPassword: string, confirmPassword: string }} payload
     */
    changePassword: async (payload) => {
        const res = await api.post('/admin/account/change-password', payload);
        return res.data;
    },
};

export const academyAuthApi = {
    login: async ({ loginId, password }) => {
        const { encryptedLoginId, encryptedPassword } = await encryptLoginPayload(loginId, password);
        const res = await api.post('/auth/login', { encryptedLoginId, encryptedPassword });
        return res.data;
    },
    findId: async ({ academyName, businessNumber }) => {
        const res = await api.post('/auth/find-id', { academyName, businessNumber });
        return res.data;
    },
    sendPasswordOtp: async ({ loginId, email }) => {
        const res = await api.post('/auth/password/send-otp', { loginId, email });
        return res.data;
    },
    verifyPasswordOtp: async ({ email, code }) => {
        const res = await api.post('/auth/password/verify-otp', { email, code });
        return res.data;
    },
    resetPassword: async ({ loginId, email, newPassword }) => {
        const res = await api.post('/auth/password/reset', { loginId, email, newPassword });
        return res.data;
    },
};

export const studentAuthApi = {
    login: async ({ loginId, password }) => {
        const { encryptedLoginId, encryptedPassword } = await encryptLoginPayload(loginId, password);
        const res = await api.post('/student/auth/login', { encryptedLoginId, encryptedPassword });
        return res.data;
    },
    findId: async ({ name, birthDate, academyCode }) => {
        const res = await api.post('/student/auth/find-id', { name, birthDate, academyCode });
        return res.data;
    },
    refresh: async ({ refreshToken }) => {
        const res = await api.post('/student/auth/refresh', { refreshToken });
        return res.data;
    },
    /**
     * 학생 로그아웃 (연속 완료 세션 종료)
     */
    logout: async () => {
        const res = await api.post('/student/auth/logout');
        return res.data;
    },
};

// 학생 - 숙제/학습 API
export const studentHomeworkApi = {
    /**
     * 학생 숙제 목록 조회
     * @param {{ search?: string, onlyAvailable?: boolean }} params
     */
    list: async (params = {}) => {
        const { search = '', onlyAvailable = false } = params;
        const res = await api.get('/student/homework/list', { params: { search, onlyAvailable } });
        return res.data;
    },
    /**
     * 학생 학습 목록 조회
     * @param {{ search?: string, onlyAvailable?: boolean }} params
     */
    learningList: async (params = {}) => {
        const { search = '', onlyAvailable = false } = params;
        const res = await api.get('/student/homework/learning/list', { params: { search, onlyAvailable } });
        return res.data;
    },
    /**
     * 숙제 상세 조회 (유닛 목록 및 진행 상황)
     * @param {number} scheduleId
     */
    detail: async (scheduleId) => {
        const res = await api.get(`/student/homework/${scheduleId}/detail`);
        return res.data;
    },
};

// 학생 - 온라인 시험 API
export const studentExamApi = {
    /**
     * 학생 온라인 시험 목록 조회
     * @param {{ search?: string, onlyAvailable?: boolean }} params
     */
    list: async (params = {}) => {
        const { search = '', onlyAvailable = false } = params;
        const res = await api.get('/student/exam/list', { params: { search, onlyAvailable } });
        return res.data;
    },
    /**
     * 시험 시작 (ExamResult 생성)
     */
    start: async (paperId) => {
        const res = await api.post('/student/exam/start', { paperId });
        return res.data;
    },
    /**
     * 문항 답안 저장 (문항별)
     * payload: { itemId, answerType: 'multiple'|'short'|'essay', selectedOptionIds?: number[], answerText?: string }
     */
    saveAnswer: async (examResultId, payload) => {
        const res = await api.post(`/student/exam/${examResultId}/answer`, payload);
        return res.data;
    },
    /**
     * 자동저장 하트비트
     */
    autosave: async (examResultId) => {
        const res = await api.post(`/student/exam/${examResultId}/autosave`);
        return res.data;
    },
    /**
     * 시험 제출
     */
    submit: async (examResultId, { cause = 'normal' } = {}) => {
        const res = await api.post(`/student/exam/${examResultId}/submit`, { cause });
        return res.data;
    },
    /**
     * 시험 결과 상세 조회 (학생용)
     * @param {number} examResultId
     */
    detail: async (examResultId) => {
        const res = await api.get(`/student/exam/result/${examResultId}/detail`);
        return res.data;
    },
};

export const studentUnitApi = {
    /**
     * 유닛 학습 데이터 조회 (활동 목록 포함)
     * @param {number} unitId
     * @param {number} scheduleId
     */
    getLearningData: async (unitId, scheduleId) => {
        const res = await api.get(`/student/unit/${unitId}/learning-data`, { params: { scheduleId } });
        return res.data;
    },
    
    /**
     * 유닛 학습 응답 저장/업데이트
     * @param {number} unitId
     * @param {Object} data - { scheduleId, responseJson, progressPercentage, status, score }
     */
    saveProgress: async (unitId, data) => {
        const res = await api.post(`/student/unit/${unitId}/save-progress`, data);
        return res.data;
    },
    
    /**
     * 유닛 진행 상황 조회
     * @param {number} unitId
     * @param {number} scheduleId
     */
    getProgress: async (unitId, scheduleId) => {
        const res = await api.get(`/student/unit/${unitId}/progress`, { params: { scheduleId } });
        return res.data;
    },
    
    /**
     * 녹음 파일 S3 업로드
     * @param {Blob} audioBlob
     */
    uploadRecording: async (audioBlob) => {
        const formData = new FormData();
        formData.append('file', audioBlob, 'recording.webm');
        const res = await api.post('/student/unit/upload-recording', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return res.data;
    },
};

export const fileApi = {
    upload: async (file, folder = 'academy') => {
        const form = new FormData();
        form.append('file', file);
        return api.post('/admin/files/upload', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            params: { folder },
        }).then(res => res.data);
    }
};

// 관리자 - 학원 관리 API
type AdminAcademyListParams = {
    q?: string;
    contractStatus?: string;
    registrationStatus?: string;
    page?: number;
    size?: number;
};

export const adminAcademyApi = {
    list: async ({ q, contractStatus, registrationStatus, page = 0, size = 10 }: AdminAcademyListParams = {}) => {
        const res = await api.get('/admin/academies', { params: { q, contractStatus, registrationStatus, page, size } });
        return res.data;
    },
    detail: async (academyCode) => {
        const res = await api.get(`/admin/academies/${academyCode}`);
        return res.data;
    },
    updateBasic: async (academyCode, payload) => {
        const res = await api.put(`/admin/academies/${academyCode}` , payload);
        return res.data;
    },
    holdBilling: async (academyCode) => {
        const res = await api.post(`/admin/academies/${academyCode}/billing-hold`);
        return res.data;
    },
    withdraw: async (academyCode) => {
        const res = await api.post(`/admin/academies/${academyCode}/withdrawal`);
        return res.data;
    },
    create: async (payload) => {
        const res = await api.post('/admin/academies', payload);
        return res.data;
    }
};

// 학원 - 자신의 정보 관리 API
export const academyInfoApi = {
    /**
     * 학원 자신의 정보 조회
     */
    getMyInfo: async () => {
        const res = await api.get('/academy/info');
        return res.data;
    },
    /**
     * 학원 자신의 정보 수정
     * @param {{ academyName?: string, representativeName?: string, representativePhone?: string, representativeEmail?: string, logoUrl?: string }} payload
     */
    updateMyInfo: async (payload) => {
        const res = await api.put('/academy/info', payload);
        return res.data;
    },
    /**
     * 학원/강사 비밀번호 변경
     * @param {{ currentPassword: string, newPassword: string, confirmPassword: string }} payload
     */
    changePassword: async (payload) => {
        const res = await api.post('/academy/info/change-password', payload);
        return res.data;
    },
};

// 관리자 - 가입신청 관리 API
export const adminRegistrationApi = {
    /**
     * @param {{ q?: any, status?: string, reapplied?: boolean }} params
     */
    list: async (params = {}) => {
        const { q, status, reapplied } = params;
        const res = await api.get('/admin/registration', { params: { q, status, reapplied } });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/admin/registration/detail/${id}`);
        return res.data;
    },
    reject: async (id, reason) => {
        const res = await api.post(`/admin/registration/${id}/reject`, { reason });
        return res.data;
    },
    approve: async (id, payload) => {
        const res = await api.post(`/admin/registration/${id}/approve`, payload);
        return res.data;
    },
    checkAcademyUrl: async (url) => {
        const res = await api.get('/admin/registration/academy-url/check', { params: { url } });
        return res.data;
    }
};

export const academyRegistrationApi = {
    signUp: async (payload) => {
        const res = await api.post('/academy/registration/sign-up', payload);
        return res.data;
    },
    checkLoginId: async (loginId) => {
        const res = await api.get('/academy/registration/login-id/duplicate-check', { params: { loginId } });
        return res.data;
    },
    checkAcademyCode: async (code) => {
        const res = await api.get('/academy/registration/academy-code/duplicate-check', { params: { code } });
        return res.data;
    },
    sendEmailOtp: async (email) => {
        const res = await api.post('/academy/registration/email/send-otp', null, { params: { email } });
        return res.data;
    },
    verifyEmailOtp: async (email, code) => {
        const res = await api.post('/academy/registration/email/verify', null, { params: { email, code } });
        return res.data;
    },
    verifyBrn: async (brn) => {
        const res = await api.get('/academy/registration/brn/verify', { params: { brn } });
        return res.data;
    },
};

export const studentRegistrationApi = {
    signUp: async (payload) => {
        const res = await api.post('/student/registration/sign-up', payload);
        return res.data;
    },
    checkLoginId: async (loginId) => {
        const res = await api.get('/student/registration/login-id/duplicate-check', { params: { loginId } });
        return res.data;
    },
    validateAcademyCode: async (code) => {
        const res = await api.get('/student/registration/academy-code/validate', { params: { code } });
        return res.data;
    },
};

// 학원 - 강사 관리 API
export const academyTeacherApi = {
    list: async (params = {}) => {
        const { academyCode, q = '', page = 0, size = 10 } = params;
        const query = { page, size };
        if (academyCode) query.academyCode = academyCode;
        if (q) query.q = q;
        const res = await api.get('/academy/teachers', { params: query });
        return res.data;
    },
    downloadTemplate: async () => {
        const res = await api.get('/academy/teachers/template', { responseType: 'blob' });
        return res.data;
    },
    create: async ({ academyCode, name, loginId, password, birthDate }) => {
        const res = await api.post('/academy/teachers', { name, loginId, password, birthDate }, { params: { academyCode } });
        return res.data;
    },
    uploadExcel: async ({ academyCode = null, file }: { academyCode?: string | null; file: File | Blob }) => {
        const form = new FormData();
        form.append('file', file);
        const config: any = {
            headers: { 'Content-Type': 'multipart/form-data' }
        };
        if (academyCode) {
            config.params = { academyCode };
        }
        const res = await api.post('/academy/teachers/upload', form, config);
        return res.data;
    },
    withdraw: async (academyTeacherId) => {
        const res = await api.post(`/academy/teachers/${academyTeacherId}/withdraw`);
        return res.data;
    },
    detail: async (academyTeacherId) => {
        const res = await api.get(`/academy/teachers/${academyTeacherId}`);
        return res.data;
    },
    update: async (academyTeacherId, payload) => {
        const res = await api.put(`/academy/teachers/${academyTeacherId}`, payload);
        return res.data;
    },
    updatePassword: async (academyTeacherId, password) => {
        const res = await api.patch(`/academy/teachers/${academyTeacherId}/password`, { password });
        return res.data;
    }
};

// 학원 - 학생 관리 API
type AcademyStudentListParams = {
    academyCode?: string;
    q?: string;
    status?: string;
    classId?: number;
    page?: number;
    size?: number;
};

export const academyStudentApi = {
    list: async ({ academyCode, q = '', status, classId, page = 0, size = 10 }: AcademyStudentListParams = {}) => {
        const params = { page, size };
        if (academyCode) params.academyCode = academyCode;
        if (q) params.q = q;
        if (status) params.status = status;
        if (classId) params.classId = classId;
        const res = await api.get('/academy/students', { params });
        return res.data;
    },
    detail: async (academyStudentId) => {
        const res = await api.get(`/academy/students/${academyStudentId}`);
        return res.data;
    },
    update: async (academyStudentId, payload) => {
        const res = await api.put(`/academy/students/${academyStudentId}`, payload);
        return res.data;
    },
    register: async ({ academyCode, name, loginId, password, birthDate, gradeLabel }) => {
        const payload = { name, loginId, password, birthDate, gradeLabel };
        if (academyCode) payload.academyCode = academyCode;
        const res = await api.post('/academy/students', payload);
        return res.data;
    },
    downloadTemplate: async ({ includeAcademyCode = false } = {}) => {
        const config = { responseType: 'blob' };
        if (includeAcademyCode) {
            config.params = { includeAcademyCode: true };
        }
        const res = await api.get('/academy/students/template', config);
        return res.data;
    },
    uploadExcel: async ({ academyCode = null, file }: { academyCode?: string | null; file: File | Blob }) => {
        const form = new FormData();
        form.append('file', file);
        const config = {
            headers: { 'Content-Type': 'multipart/form-data' }
        };
        if (academyCode) {
            config.params = { academyCode };
        }
        const res = await api.post('/academy/students/upload', form, config);
        return res.data;
    },
    learningHistory: async ({ academyStudentId, category = 'learning', page = 0, size = 10, search = '', onlyAvailable = false } = {}) => {
        const res = await api.get(`/academy/students/${academyStudentId}/learning-history`, { params: { category, page, size, search, onlyAvailable } });
        return res.data;
    },
    withdraw: async (academyStudentId) => {
        const res = await api.post(`/academy/students/${academyStudentId}/withdraw`);
        return res.data;
    },
    changePassword: async (academyStudentId, payload) => {
        const res = await api.post(`/academy/students/${academyStudentId}/password`, payload);
        return res.data;
    },
};

// 관리자 - 학생 상세 부가 정보 API
export const adminStudentApi = {
    listAll: async ({ page = 0, size = 10, q = '', academyCode }: { page?: number; size?: number; q?: string; academyCode?: string } = {}) => {
        const params: Record<string, any> = { page, size };
        if (q) params.q = q;
        if (academyCode) params.academyCode = academyCode;
        const res = await api.get('/admin/students', { params });
        return res.data;
    },
    updatePassword: async (academyStudentId, payload) => {
        const res = await api.post(`/admin/students/${academyStudentId}/password`, payload);
        return res.data;
    },
    learningHistory: async ({ academyStudentId, page = 0, size = 10, category = undefined }) => {
        const res = await api.get(`/admin/students/${academyStudentId}/learning-history`, { params: { page, size, category } });
        return res.data;
    },
    trophies: async (academyStudentId) => {
        const res = await api.get(`/admin/students/${academyStudentId}/trophies`);
        return res.data;
    },
    badges: async (academyStudentId) => {
        const res = await api.get(`/admin/students/${academyStudentId}/badges`);
        return res.data;
    },
    scheduleDetail: async (scheduleId) => {
        const res = await api.get(`/admin/students/learning-schedules/${scheduleId}/detail`);
        return res.data;
    }
};

// 학원 - 클래스 관리 API (옵션용 최소 구현)
export const academyClassApi = {
    list: async ({ academyCode, page = 0, size = 100 }) => {
        const res = await api.get('/academy/classes', { params: { academyCode, page, size } });
        return res.data;
    },
    create: async ({ academyCode, name }) => {
        const payload = { name };
        const res = await api.post('/academy/classes', payload, { params: { academyCode } });
        return res.data;
    },
    update: async ({ id, name }) => {
        const res = await api.put(`/academy/classes/${id}`, { name });
        return res.data;
    },
    remove: async (id) => {
        const res = await api.delete(`/academy/classes/${id}`);
        return res.data;
    }
};

// 학원 - 단어 목록 API
export const academyWordApi = {
    /**
     * @param {{ q?: string, gender?: string, age?: string, speed?: string, pitch?: string, academyCode?: string, page?: number, size?: number }} params
     */
    list: async (params = {}) => {
        const { q = '', gender = '', age = '', speed = '', pitch = '', academyCode = '', page = 0, size = 10 } = params;
        const res = await api.get('/academy/words', { params: { q, gender, age, speed, pitch, academyCode, page, size } });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/academy/words/${id}`);
        return res.data;
    },
    create: async ({ word, meaning, gender, age, speed, pitch, registrant = '', registrantUserId, academyCode }) => {
        const res = await api.post('/academy/words', { word, meaning, gender, age, speed, pitch, registrant, registrantUserId, academyCode });
        return res.data;
    },
    update: async (id, payload) => {
        const res = await api.put(`/academy/words/${id}`, payload);
        return res.data;
    },
    exists: async ({ word, gender, age, speed, pitch }) => {
        const res = await api.get('/academy/words/exists', { params: { word, gender, age, speed, pitch } });
        return res.data;
    },
    preview: async ({ word, gender, age, speed, pitch }) => {
        const res = await api.post('/academy/words/preview', { word, gender, age, speed, pitch }, { responseType: 'blob' });
        return res.data;
    },
};

// 학원 - 단어장 API
export const academyWordbookApi = {
    list: async ({
        q = '',
        isPublic = undefined,
        academyCode = '',
        page = 0,
        size = 10,
    }: { q?: string; isPublic?: boolean | null; academyCode?: string; page?: number; size?: number } = {}) => {
        const res = await api.get('/academy/wordbooks', { params: { q, isPublic, academyCode, page, size } });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/academy/wordbooks/${id}`);
        return res.data;
    },
    create: async ({ title, isPublic = true, registrant = '', wordIds = [], registrantUserId, academyCode }) => {
        const res = await api.post('/academy/wordbooks', { title, isPublic, registrant, wordIds, registrantUserId, academyCode });
        return res.data;
    },
    update: async (id, payload) => {
        const res = await api.put(`/academy/wordbooks/${id}`, payload);
        return res.data;
    },
};

// 학생 - 단어장 API
export const studentWordbookApi = {
    list: async ({ q = '', page = 0, size = 10 } = {}) => {
        const res = await api.get('/student/wordbooks', { params: { q, page, size } });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/student/wordbooks/${id}`);
        return res.data;
    },
    saveSession: async (id, { maxStreak, totalItems } = {}) => {
        const res = await api.post(`/student/wordbooks/${id}/sessions`, { maxStreak, totalItems });
        return res.data;
    },
    upsertProgress: async (id, { maxStreak } = {}) => {
        const res = await api.post(`/student/wordbooks/${id}/progress`, { maxStreak });
        return res.data;
    },
    /**
     * 개별 단어 정답/오답 체크 (단어 연속 완료 세션)
     * @param {number} id - 단어장 ID
     * @param {Object} data - { isCorrect: boolean }
     */
    checkWord: async (id, data) => {
        const res = await api.post(`/student/wordbooks/${id}/words/check`, data);
        return res.data;
    },
};

// 관리자 - 탈퇴 신청 관리 API
export const adminWithdrawalApi = {
    list: async ({ academyCode, status, page = 0, size = 10 }) => {
        const res = await api.get('/admin/withdrawal', { params: { academyCode, status, page, size } });
        return res.data;
    },
    approve: async (ids = []) => {
        const res = await api.post('/admin/withdrawal/approve', { ids });
        return res.data;
    }
};

// 관리자 - 문장(TTS) API
export const adminSentenceApi = {
    detail: async (id) => {
        const res = await api.get(`/admin/sentences/${id}`);
        return res.data;
    },
    update: async (id, payload) => {
        const res = await api.put(`/admin/sentences/${id}`, payload);
        return res.data;
    },
    list: async ({ q = '', gender = '', age = '', speed = '', pitch = '', isPublic = undefined, page = 0, size = 10 } = {}) => {
        const res = await api.get('/admin/sentences', { params: { q, gender, age, speed, pitch, isPublic, page, size } });
        return res.data;
    },
    preview: async ({ sentence, gender, age, speed, pitch }) => {
        const res = await api.post('/admin/sentences/preview', { sentence, gender, age, speed, pitch }, { responseType: 'blob' });
        return res.data; // Blob(MP3)
    },
    usage: async (id) => {
        const res = await api.get(`/admin/sentences/${id}/usage`);
        return res.data;
    },
    create: async ({ sentence, translation, gender, age, speed, pitch, isPublic = true, registrant = '' }) => {
        const res = await api.post('/admin/sentences', { sentence, translation, gender, age, speed, pitch, isPublic, registrant });
        return res.data;
    }
};

// 관리자 - 단어(TTS) API
export const adminWordApi = {
    detail: async (id) => {
        const res = await api.get(`/admin/words/${id}`);
        return res.data;
    },
    exists: async ({ word, gender, age, speed, pitch }) => {
        const res = await api.get('/admin/words/exists', { params: { word, gender, age, speed, pitch } });
        return res.data;
    },
    update: async (id, payload) => {
        const res = await api.put(`/admin/words/${id}`, payload);
        return res.data;
    },
    list: async ({ q = '', gender = '', age = '', speed = '', pitch = '', page = 0, size = 10 } = {}) => {
        const res = await api.get('/admin/words', { params: { q, gender, age, speed, pitch, page, size } });
        return res.data;
    },
    preview: async ({ word, gender, age, speed, pitch }) => {
        const res = await api.post('/admin/words/preview', { word, gender, age, speed, pitch }, { responseType: 'blob' });
        return res.data; // Blob(MP3)
    },
    create: async ({ word, meaning, gender, age, speed, pitch, registrant = '' }) => {
        const res = await api.post('/admin/words', { word, meaning, gender, age, speed, pitch, registrant });
        return res.data;
    }
};

// 관리자 - 단어장 API
export const adminWordbookApi = {
    list: async ({ q = '', isPublic, page = 0, size = 10 } = {}) => {
        const res = await api.get('/admin/wordbooks', { params: { q, isPublic, page, size } });
        return res.data;
    },
    create: async ({ title, isPublic = true, registrant = '', wordIds = [] }) => {
        const res = await api.post('/admin/wordbooks', { title, isPublic, registrant, wordIds });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/admin/wordbooks/${id}`);
        return res.data;
    },
    update: async (id, payload) => {
        const res = await api.put(`/admin/wordbooks/${id}`, payload);
        return res.data;
    }
};

// 관리자 - 유닛 API
export const adminUnitApi = {
    list: async ({ q = '', page = 0, size = 10 } = {}) => {
        const res = await api.get('/admin/units', { params: { q, page, size } });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/admin/units/${id}`);
        return res.data;
    },
    hasLearningHistory: async (id) => {
        const res = await api.get(`/admin/units/${id}/has-learning-history`);
        return res.data;
    },
    create: async (payload) => {
        const res = await api.post('/admin/units', payload);
        return res.data;
    },
    update: async (id, payload) => {
        const res = await api.put(`/admin/units/${id}`, payload);
        return res.data;
    }
};

// 관리자 - 학습 스케줄 API
export const adminLearningScheduleApi = {
    list: async ({ academyId, academyCode, start, end }) => {
        const params = { start, end };
        if (academyId !== undefined && academyId !== null) {
            params.academyId = academyId;
        } else if (academyCode) {
            params.academyCode = academyCode;
        }
        const res = await api.get('/admin/learning/schedules', { params });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/admin/learning/schedules/${id}`);
        return res.data;
    },
    preview: async ({ academyId, start, end }) => {
        const res = await api.get('/admin/learning/schedules/preview', { params: { academyId, start, end } });
        return res.data;
    },
    save: async ({ academyId, academyCode, items, unitIds, unitLinks }) => {
        const payload = { items };
        if (academyId !== undefined) {
            payload.academyId = academyId;
        }
        if (academyCode !== undefined) {
            payload.academyCode = academyCode;
        }
        if (Array.isArray(unitLinks) && unitLinks.length > 0) {
            payload.unitLinks = unitLinks;
        }
        if (Array.isArray(unitIds) && unitIds.length > 0) {
            payload.unitIds = unitIds;
        }
        const res = await api.post('/admin/learning/schedules', payload);
        return res.data;
    },
    update: async (id, payload) => {
        const res = await api.put(`/admin/learning/schedules/${id}`, payload);
        return res.data;
    },
    remove: async (id) => {
        const res = await api.delete(`/admin/learning/schedules/${id}`);
        return res.data;
    },
    linkUnits: async (id, { unitIds = [], unitLinks } = {}) => {
        const body = {};
        if (Array.isArray(unitLinks) && unitLinks.length > 0) {
            body.unitLinks = unitLinks;
        }
        if (Array.isArray(unitIds) && unitIds.length > 0) {
            body.unitIds = unitIds;
        }
        const res = await api.post(`/admin/learning/schedules/${id}/units`, body);
        return res.data;
    },
    deleteByStudent: async ({ academyId, title, startDate, endDate, studentUserId, groupUuid }) => {
        const res = await api.post('/admin/learning/schedules/delete-by-student', { academyId, title, startDate, endDate, studentUserId, groupUuid });
        return res.data;
    },
    hasProgress: async (id) => {
        const res = await api.get(`/admin/learning/schedules/${id}/has-progress`);
        return res.data;
    },
    hasUnitProgress: async (id, unitId) => {
        const res = await api.get(`/admin/learning/schedules/${id}/units/${unitId}/has-progress`);
        return res.data;
    },
    hasStudentProgress: async (id, studentUserId) => {
        const res = await api.get(`/admin/learning/schedules/${id}/students/${studentUserId}/has-progress`);
        return res.data;
    },
    adjust: async (id, payload) => {
        // payload: { mode: 'drag' | 'resize', deltaDays?: number, newEndDate?: 'YYYY-MM-DD' }
        const res = await api.post(`/admin/learning/schedules/${id}/adjust`, payload);
        return res.data;
    },
    copyToStudents: async (id, payload) => {
        const targetStudentUserIds = (payload && Array.isArray(payload.targetStudentUserIds)) ? payload.targetStudentUserIds : [];
        const res = await api.post(`/admin/learning/schedules/${id}/copy`, { targetStudentUserIds });
        return res.data;
    },
    listStudentsInSameLearning: async (id) => {
        const res = await api.get(`/admin/learning/schedules/${id}/students`);
        return res.data;
    },
    /**
     * @param {{ academyId?: number, academyCode?: string, search?: string, gradingStatus?: string, onlyNeedGrading?: boolean }} [options]
     */
    gradingList: async (options = {}) => {
        const { academyId, academyCode, search, gradingStatus, onlyNeedGrading } = options;
        const params = {};
        if (academyId !== undefined && academyId !== null) {
            params.academyId = academyId;
        } else if (academyCode) {
            params.academyCode = academyCode;
        }
        if (search) params.search = search;
        if (gradingStatus) params.gradingStatus = gradingStatus;
        if (onlyNeedGrading) params.onlyNeedGrading = true;
        const res = await api.get('/admin/learning/grading', { params });
        return res.data;
    },
    gradingDetail: async (scheduleId) => {
        const res = await api.get(`/admin/learning/grading/${scheduleId}/detail`);
        return res.data;
    },
    completeGrading: async (scheduleId, gradingData) => {
        const res = await api.post(`/admin/learning/grading/${scheduleId}/complete`, gradingData);
        return res.data;
    },
    resendReport: async (scheduleId) => {
        const res = await api.post(`/admin/learning/grading/${scheduleId}/resend-report`);
        return res.data;
    },
    aiGradeDictation: async (scheduleId, payload) => {
        const res = await api.post(`/admin/learning/grading/${scheduleId}/dictation/ai-grade`, payload);
        return res.data;
    },
    aiGradeFreeWriting: async (scheduleId, { inputTexts, topic }) => {
        const payload = { inputTexts };
        if (topic !== undefined) {
            payload.topic = topic;
        }
        const res = await api.post(`/admin/learning/grading/${scheduleId}/free-writing/ai-grade`, payload);
        return res.data;
    },
};

// 학원 - 학습 스케줄 API
export const academyLearningScheduleApi = {
    list: async ({ start, end }) => {
        const res = await api.get('/academy/learning/schedules', { params: { start, end } });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/academy/learning/schedules/${id}`);
        return res.data;
    },
    preview: async ({ start, end }) => {
        const res = await api.get('/academy/learning/schedules/preview', { params: { start, end } });
        return res.data;
    },
    listStudentsInSameLearning: async (id) => {
        const res = await api.get(`/academy/learning/schedules/${id}/students`);
        return res.data;
    },
    adjust: async (id, payload) => {
        // payload: { mode: 'drag' | 'resize', deltaDays?: number, newEndDate?: 'YYYY-MM-DD' }
        const res = await api.post(`/academy/learning/schedules/${id}/adjust`, payload);
        return res.data;
    },
    copyToStudents: async (id, payload) => {
        const targetStudentUserIds = (payload && Array.isArray(payload.targetStudentUserIds)) ? payload.targetStudentUserIds : [];
        const res = await api.post(`/academy/learning/schedules/${id}/copy`, { targetStudentUserIds });
        return res.data;
    },
    deleteByStudent: async ({ title, startDate, endDate, studentUserId, groupUuid }) => {
        const res = await api.post('/academy/learning/schedules/delete-by-student', { title, startDate, endDate, studentUserId, groupUuid });
        return res.data;
    },
    remove: async (id) => {
        const res = await api.delete(`/academy/learning/schedules/${id}`);
        return res.data;
    },
    save: async ({ learningId, items }) => {
        const payload = { items };
        if (learningId !== undefined) Object.assign(payload, { learningId });
        const res = await api.post('/academy/learning/schedules', payload);
        return res.data;
    },
    update: async (id, payload) => {
        const res = await api.put(`/academy/learning/schedules/${id}`, payload);
        return res.data;
    },
    linkUnits: async (id, { unitIds = [], unitLinks } = {}) => {
        const body = {};
        if (Array.isArray(unitLinks) && unitLinks.length > 0) {
            body.unitLinks = unitLinks;
        }
        if (Array.isArray(unitIds) && unitIds.length > 0) {
            body.unitIds = unitIds;
        }
        const res = await api.post(`/academy/learning/schedules/${id}/units`, body);
        return res.data;
    },
    hasUnitProgress: async (id, unitId) => {
        const res = await api.get(`/academy/learning/schedules/${id}/units/${unitId}/has-progress`);
        return res.data;
    },
    hasStudentProgress: async (id, studentUserId) => {
        const res = await api.get(`/academy/learning/schedules/${id}/students/${studentUserId}/has-progress`);
        return res.data;
    },
    // 숙제 채점 리스트 조회
    gradingList: async (options = {}) => {
        const { search, gradingStatus, onlyNeedGrading = false } = options;
        const params = {};
        if (search) params.search = search;
        if (gradingStatus) params.gradingStatus = gradingStatus;
        if (onlyNeedGrading) params.onlyNeedGrading = true;
        const res = await api.get('/academy/learning/schedules/grading', { params });
        return res.data;
    },
    // 성적표 재발송
    resendReport: async (scheduleId) => {
        const res = await api.post(`/academy/learning/schedules/${scheduleId}/resend-grading-report`);
        return res.data;
    },
    // 채점 상세 조회
    gradingDetail: async (scheduleId) => {
        const res = await api.get(`/academy/learning/schedules/grading/${scheduleId}/detail`);
        return res.data;
    },
    completeGrading: async (scheduleId, gradingData) => {
        const res = await api.post(`/academy/learning/schedules/${scheduleId}/complete-grading`, gradingData);
        return res.data;
    },
    // 딕테이션 AI 채점 (STT)
    aiGradeDictation: async (scheduleId, { recordingUrl, correctWord }) => {
        const res = await api.post(`/academy/learning/schedules/${scheduleId}/dictation/ai-grade`, { recordingUrl, correctWord });
        return res.data;
    },
    // 프리라이팅 AI 채점 (문법 검사)
    aiGradeFreeWriting: async (scheduleId, { inputTexts, topic, hints }) => {
        const payload = { inputTexts };
        if (topic !== undefined) {
            payload.topic = topic;
        }
        if (hints !== undefined) {
            payload.hints = hints;
        }
        const res = await api.post(`/academy/learning/schedules/${scheduleId}/free-writing/ai-grade`, payload);
        return res.data;
    }
};

// 학원 - 학습 마스터 API
export const academyLearningApi = {
    create: async ({ title, category, backgroundColor, borderColor, maxUnitsPerDay, scoringRequired, unitIds = [] }) => {
        const res = await api.post('/academy/learnings', { title, category, backgroundColor, borderColor, maxUnitsPerDay, scoringRequired, unitIds });
        return res.data;
    },
    listUnits: async (learningId) => {
        const res = await api.get(`/academy/learnings/${learningId}/units`);
        return res.data;
    },
};

// 학원 - 교재 미리보기/조회 API
export const academyTextbookApi = {
    list: async ({ q = '', category = '', page = 0, size = 10 } = {}) => {
        const res = await api.get('/academy/textbooks', { params: { q, category, page, size } });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/academy/textbooks/${id}`);
        return res.data;
    },
    listUnits: async (id) => {
        const res = await api.get(`/academy/textbooks/${id}/units`);
        return res.data;
    },
    previewUnit: async (unitId) => {
        const res = await api.get(`/academy/units/${unitId}/preview`);
        return res.data;
    }
};

// 관리자 - 학습 마스터 API
// adminLearningApi 제거됨: Learning 테이블 폐기로 인해 더 이상 사용하지 않음
// 학습/숙제는 adminLearningScheduleApi를 통해 직접 관리됩니다.

// 관리자 - 교재(Textbook) API
export const adminTextbookApi = {
    list: async ({ q = '', category = '', page = 0, size = 10 } = {}) => {
        const res = await api.get('/admin/textbooks', { params: { q, category, page, size } });
        return res.data;
    },
    detail: async (id) => {
        const res = await api.get(`/admin/textbooks/${id}`);
        return res.data;
    },
    create: async (payload) => {
        const res = await api.post('/admin/textbooks', payload);
        return res.data;
    },
    update: async (id, payload) => {
        const res = await api.put(`/admin/textbooks/${id}`, payload);
        return res.data;
    },
    hasLearningHistory: async (id) => {
        const res = await api.get(`/admin/textbooks/${id}/usage`);
        return res.data;
    },
    remove: async (id) => {
        const res = await api.delete(`/admin/textbooks/${id}`);
        return res.data;
    },
    linkUnits: async (id, { unitIds = [] } = {}) => {
        const res = await api.post(`/admin/textbooks/${id}/units`, { unitIds });
        return res.data;
    },
    listUnits: async (id) => {
        const res = await api.get(`/admin/textbooks/${id}/units`);
        return res.data;
    },
    findByUnit: async (unitId) => {
        const res = await api.get(`/admin/textbooks/by-unit/${unitId}`);
        return res.data;
    },
    detail: async (textbookId) => {
        const res = await api.get(`/admin/textbooks/${textbookId}`);
        return res.data;
    }
};

// 관리자 - 유료 시험지(Paper) API
// 관리자 - 파일 업로드 API
export const adminFileApi = {
    /**
     * Base64 이미지 업로드
     * @param {string} base64Data - data:image/png;base64,... 형식
     * @param {string} folder - 저장 폴더명 (기본: paper-images)
     */
    uploadBase64: async (base64Data, folder = 'paper-images') => {
        const res = await api.post('/admin/files/upload-base64', { base64Data, folder });
        return res.data;
    },
};

export const adminPaperApi = {
    /**
     * @param {{ q?: string, isPublic?: boolean, registrant?: string, page?: number, size?: number }} params
     */
    list: async (params = {}) => {
        const { q = '', isPublic = undefined, registrant = '', page = 0, size = 10 } = params;
        const res = await api.get('/admin/papers', { params: { q, isPublic, registrant, page, size } });
        return res.data;
    },
    /**
     * @param {number} id
     */
    detail: async (id) => {
        const res = await api.get(`/admin/papers/${id}`);
        return res.data;
    },
    /**
     * @param {{ title: string, isPublic?: boolean, price?: number, registrant?: string, trophyConfig?: string, timeLimit?: number, aiScoring?: boolean, questions?: string }} payload
     */
    create: async (payload) => {
        const res = await api.post('/admin/papers', payload);
        return res.data;
    },
    /**
     * @param {number} id
     * @param {{ title?: string, isPublic?: boolean, price?: number, registrant?: string, trophyConfig?: string, timeLimit?: number, aiScoring?: boolean, questions?: string }} payload
     */
    update: async (id, payload) => {
        const res = await api.put(`/admin/papers/${id}`, payload);
        return res.data;
    },
    /**
     * @param {number} id
     */
    delete: async (id) => {
        const res = await api.delete(`/admin/papers/${id}`);
        return res.data;
    },
};
// 관리자 - 유료 시험 판매내역 API
export const adminPaperSalesApi = {
    /**
     * 판매내역 목록
     * @param {{ paperId?: number, page?: number, size?: number }} params
     */
    list: async (params = {}) => {
        const { paperId = undefined, page = 0, size = 10 } = params;
        const res = await api.get('/admin/papers/sales', { params: { paperId, page, size } });
        return res.data;
    },
};

const academyByUrlCache = new Map();
const academyByUrlPending = new Map();

const normalizeSlugKey = (value = '') => {
    if (!value) return '';
    return String(value).trim().toLowerCase();
};

// 공개 - 학원 정보 조회 API (slug)
export const publicAcademyApi = {
    /**
     * 학원 URL(slug)로 학원 공개 정보 조회
     * @param {string} url
     */
    getByUrl: async (url) => {
        const key = normalizeSlugKey(url);
        if (key && academyByUrlCache.has(key)) {
            return academyByUrlCache.get(key);
        }
        if (key && academyByUrlPending.has(key)) {
            return academyByUrlPending.get(key);
        }
        const request = api
            .get('/public/academy/by-url', { params: { url } })
            .then((res) => {
        const body = res?.data || res;
        const payload = body?.data || body || {};
                if (key) {
                    academyByUrlCache.set(key, payload);
                }
        return payload;
            })
            .catch((err) => {
                if (key) {
                    academyByUrlCache.delete(key);
                }
                throw err;
            })
            .finally(() => {
                if (key) {
                    academyByUrlPending.delete(key);
                }
            });
        if (key) {
            academyByUrlPending.set(key, request);
        }
        return request;
    },
    /**
     * 학원 코드로 학원 공개 정보 조회
     * @param {string} code
     */
    getByCode: async (code) => {
        const res = await api.get('/public/academy/by-code', { params: { code } });
        const body = res?.data || res;
        const payload = body?.data || body || {};
        return payload;
    },
};

export const sessionApi = {
    getSlug: async () => {
        const res = await api.get('/session/slug');
        return res.data;
    },
};

// 학원 - 유료 시험지(Paper) API
export const academyPaperApi = {
    /**
     * @param {{ q?: string, isPublic?: boolean, registrant?: string, questionType?: string, page?: number, size?: number }} params
     */
    list: async (params = {}) => {
        const { q = '', isPublic = undefined, registrant = '', questionType = undefined, page = 0, size = 10 } = params;
        const queryParams = { q, isPublic, registrant, page, size };
        if (questionType !== undefined && questionType !== null && questionType !== '') {
            queryParams.questionType = questionType;
        }
        const res = await api.get('/academy/papers', { params: queryParams });
        return res.data;
    },
    /**
     * 학원이 구매한 시험지 목록 조회 (마이페이지용)
     * @param {{ q?: string, aiScoring?: boolean, page?: number, size?: number }} params
     */
    getPurchased: async (params = {}) => {
        const { q = '', aiScoring = undefined, page = 0, size = 10 } = params;
        const res = await api.get('/academy/papers/purchased', { params: { q, aiScoring, page, size } });
        return res.data;
    },
    /**
     * @param {number} id
     */
    detail: async (id) => {
        const res = await api.get(`/academy/papers/${id}`);
        return res.data;
    },
    /**
     * 시험지 구매
     * @param {number} paperId
     * @param {{ academyId: number, quantity: number, paymentMethod?: string }} data
     */
    purchase: async (paperId, data) => {
        const res = await api.post(`/academy/papers/${paperId}/purchase`, data);
        return res.data;
    },
    /**
     * 응시권 통계 조회 (JWT에서 학원 정보 추출)
     * @param {number} paperId
     */
    getTicketStats: async (paperId, params = {}) => {
        const res = await api.get(`/academy/papers/${paperId}/tickets/stats`, { params });
        return res.data;
    },
    /**
     * 응시권 환불 처리
     * @param {number} paperId
     * @param {{ quantity: number, saleId?: number }} data
     */
    refundTickets: async (paperId, data) => {
        const res = await api.post(`/academy/papers/${paperId}/tickets/refund`, data);
        return res.data;
    },
    /**
     * 시험지에 배정된 학생 목록 조회
     * @param {number} paperId
     */
    getAssignedStudents: async (paperId) => {
        const res = await api.get(`/academy/papers/${paperId}/assigned-students`);
        return res.data;
    },
    /**
     * 여러 학생에게 응시권 일괄 배정
     * @param {number} paperId
     * @param {{ studentUserIds: number[] }} data
     */
    assignStudents: async (paperId, data) => {
        const res = await api.post(`/academy/papers/${paperId}/assign-students`, data);
        return res.data;
    },
    /**
     * 응시권 회수 (배정 취소)
     * @param {number} paperId
     * @param {number} ticketId
     */
    revokeTicket: async (paperId, ticketId) => {
        const res = await api.delete(`/academy/papers/${paperId}/assigned-students/${ticketId}`);
        return res.data;
    },
    /**
     * 토스페이먼츠 결제 승인
     * @param {{ paymentKey: string, orderId: string, amount: number, paperId: number, quantity: number, academyId: number }} data
     */
    confirmPayment: async (data) => {
        const res = await api.post('/academy/papers/confirm-payment', data);
        return res.data;
    },
};

// 학원 - 정기결제(구독) API
export const academySubscriptionApi = {
    /**
     * 현재 구독 상태 조회 (정상/대기/미납/유예 등)
     */
    status: async (params = {}) => {
        const res = await api.get('/academy/subscription/status', { params });
        return res.data;
    },
    /**
     * 실사용 인원 수 조회 (재원중 학생 수)
     */
    studentCount: async (params = {}) => {
        const res = await api.get('/academy/subscription/student-count', { params });
        return res.data;
    },
    /**
     * 정기결제 초기화(좌석/요금제 선택) - 빌링키 발급 전 단계
     */
    init: async (payload = {}) => {
        const res = await api.post('/academy/subscription/init', payload);
        return res.data;
    },
    /**
     * 빌링키 저장/발급 (authKey 또는 billingKey 전달)
     * payload: { academyCode?, authKey?, customerKey?, billingKey? }
     */
    setBillingKey: async (payload = {}) => {
        const res = await api.post('/academy/subscription/billing-key', payload);
        return res.data;
    },
    /**
     * 즉시 청구(첫 결제 또는 수동 재시도)
     */
    charge: async (payload = {}) => {
        const res = await api.post('/academy/subscription/charge', payload);
        return res.data;
    },
    /**
     * 구독 결제 이력 목록
     */
    history: async (params = {}) => {
        const res = await api.get('/academy/subscription/history', { params });
        return res.data;
    },
    /**
     * 요금제 변경 (상향/하향 - 일할 계산하여 차액 결제/환불)
     * payload: { academyCode, newPlan, newSeatRange? }
     */
    previewChangePlan: async (payload = {}) => {
        const res = await api.post('/academy/subscription/preview-change-plan', payload);
        return res.data;
    },
    changePlan: async (payload = {}) => {
        const res = await api.post('/academy/subscription/change-plan', payload);
        return res.data;
    },
    /**
     * 통합 결제 이력 조회 (구독 + 시험지)
     * payload: { academyCode, search?, startDate?, endDate?, page?, pageSize? }
     */
    paymentHistory: async (payload = {}) => {
        const params = new URLSearchParams();
        if (payload.academyCode) params.append('academyCode', payload.academyCode);
        if (payload.search) params.append('search', payload.search);
        if (payload.startDate) params.append('startDate', payload.startDate);
        if (payload.endDate) params.append('endDate', payload.endDate);
        if (payload.page) params.append('page', payload.page);
        if (payload.pageSize) params.append('pageSize', payload.pageSize);
        
        const res = await api.get(`/academy/subscription/payment-history?${params.toString()}`);
        return res.data;
    },
};

// 학원 - 대시보드 요약 API
export const academyDashboardApi = {
    summary: async () => {
        const res = await api.get('/academy/dashboard/summary');
        return res.data;
    },
};

// 관리자 - 트로피/뱃지 API
export const adminTrophyApi = {
    /**
     * @param {{ type?: string }} params
     */
    list: async (params = {}) => {
        const { type = '' } = params;
        const res = await api.get('/admin/trophy-badges', { params: { type } });
        return res.data;
    },
};

// 관리자 - 단어/문장 통합 검색 API
export const adminWordSentenceApi = {
    /**
     * @param {{ q?: string, page?: number, size?: number }} params
     */
    search: async (params = {}) => {
        const { q = '', page = 0, size = 50 } = params;
        const res = await api.get('/admin/word-sentence-search', { params: { q, page, size } });
        return res.data;
    },
};

// 관리자 - 단어시험 API
export const adminWordExamApi = {
    /**
     * @param {{ q?: string, type?: string, registrant?: string, page?: number, size?: number }} params
     */
    list: async (params = {}) => {
        const { q = '', type = undefined, registrant = '', page = 0, size = 10 } = params;
        const res = await api.get('/admin/word-exams', { params: { q, type, registrant, page, size } });
        return res.data;
    },
    /**
     * @param {number} id
     */
    detail: async (id) => {
        const res = await api.get(`/admin/word-exams/${id}`);
        return res.data;
    },
    /**
     * @param {{ name: string, examType: 'word'|'meaning', wordSubmitMethod: 'wordbook'|'individual', words?: string, wordbookIds?: string, isPublic?: boolean, registrant?: string }} payload
     */
    create: async (payload) => {
        const res = await api.post('/admin/word-exams', payload);
        return res.data;
    },
    /**
     * @param {number} id
     * @param {{ name?: string, examType?: 'word'|'meaning', wordSubmitMethod?: 'wordbook'|'individual', words?: string, wordbookIds?: string, isPublic?: boolean, registrant?: string }} payload
     */
    update: async (id, payload) => {
        const res = await api.put(`/admin/word-exams/${id}`, payload);
        return res.data;
    },
    /**
     * @param {number} id
     */
    delete: async (id) => {
        const res = await api.delete(`/admin/word-exams/${id}`);
        return res.data;
    },
};

// 학원 - 단어시험 API
export const academyWordExamApi = {
    /**
     * @param {{ q?: string, type?: string, registrant?: string, isPublic?: boolean, academyCode?: string, page?: number, size?: number }} params
     */
    list: async (params = {}) => {
        const { q = '', type = undefined, registrant = '', isPublic = undefined, academyCode = '', page = 0, size = 10 } = params;
        const res = await api.get('/academy/word-exams', { params: { q, type, registrant, isPublic, academyCode, page, size } });
        return res.data;
    },
    /**
     * @param {number} id
     */
    detail: async (id) => {
        const res = await api.get(`/academy/word-exams/${id}`);
        return res.data;
    },
    /**
     * @param {{ name: string, examType: 'word'|'meaning', wordSubmitMethod: 'wordbook'|'individual', words?: string, wordbookIds?: string, isPublic?: boolean, registrant?: string }} payload
     */
    create: async (payload) => {
        const res = await api.post('/academy/word-exams', payload);
        return res.data;
    },
    /**
     * @param {number} id
     * @param {{ name?: string, examType?: 'word'|'meaning', wordSubmitMethod?: 'wordbook'|'individual', words?: string, wordbookIds?: string, isPublic?: boolean, registrant?: string }} payload
     */
    update: async (id, payload) => {
        const res = await api.put(`/academy/word-exams/${id}`, payload);
        return res.data;
    },
    /**
     * @param {number} id
     */
    delete: async (id) => {
        const res = await api.delete(`/academy/word-exams/${id}`);
        return res.data;
    },
    /**
     * @param {number} id
     */
    downloadExam: async (id) => {
        const res = await api.get(`/academy/word-exams/${id}/download-exam`, { responseType: 'blob' });
        return res.data;
    },
    /**
     * @param {number} id
     */
    downloadAnswer: async (id) => {
        const res = await api.get(`/academy/word-exams/${id}/download-answer`, { responseType: 'blob' });
        return res.data;
    },
};

// 관리자 - 트로피/뱃지 API
export const adminTrophyBadgeApi = {
    /**
     * 트로피/뱃지 마스터 데이터 목록 조회
     * @param {{ type?: string }} params
     */
    listMaster: async (params = {}) => {
        const { type } = params;
        const res = await api.get('/admin/trophy-badges', { params: { type } });
        return res.data;
    },
    /**
     * 트로피/뱃지 마스터 상세 조회
     * @param {number} id
     */
    getMasterDetail: async (id) => {
        const res = await api.get(`/admin/trophy-badges/${id}`);
        return res.data;
    },
    /**
     * 트로피/뱃지 마스터 생성
     * @param {{ name: string, description: string, iconUrl: string, type: string, grade: number, criteriaJson?: string }} payload
     */
    createMaster: async (payload) => {
        const res = await api.post('/admin/trophy-badges', payload);
        return res.data;
    },
    /**
     * 트로피/뱃지 마스터 수정
     * @param {number} id
     * @param {{ name?: string, description?: string, iconUrl?: string, type?: string, grade?: number, criteriaJson?: string }} payload
     */
    updateMaster: async (id, payload) => {
        const res = await api.put(`/admin/trophy-badges/${id}`, payload);
        return res.data;
    },
    /**
     * 트로피/뱃지 마스터 삭제
     * @param {number} id
     */
    deleteMaster: async (id) => {
        const res = await api.delete(`/admin/trophy-badges/${id}`);
        return res.data;
    },
    /**
     * 트로피/뱃지 수여 목록 조회
     * @param {{ type?: string, studentUserId?: number, grantedById?: number, page?: number, size?: number }} params
     */
    listAwards: async (params = {}) => {
        const { type, studentUserId, grantedById, page = 0, size = 10 } = params;
        const res = await api.get('/admin/trophy-badges/awards', { params: { type, studentUserId, grantedById, page, size } });
        return res.data;
    },
    /**
     * 트로피/뱃지 수여 상세 조회
     * @param {number} id
     */
    getAwardDetail: async (id) => {
        const res = await api.get(`/admin/trophy-badges/awards/${id}`);
        return res.data;
    },
    /**
     * 트로피/뱃지 수여
     * @param {{ trophyBadgeId: number, studentUserIds: number[], reason: string, awardedAt?: string }} payload
     * @note grantedBy는 백엔드에서 현재 로그인한 관리자로 자동 설정됩니다.
     */
    createAward: async (payload) => {
        const res = await api.post('/admin/trophy-badges/awards', payload);
        return res.data;
    },
    /**
     * 트로피/뱃지 수여 정보 수정
     * @param {number} id
     * @param {{ trophyBadgeId?: number, studentUserId?: number, reason?: string, awardedAt?: string }} payload
     * @note grantedBy는 백엔드에서 현재 로그인한 관리자로 자동 갱신됩니다.
     */
    updateAward: async (id, payload) => {
        const res = await api.put(`/admin/trophy-badges/awards/${id}`, payload);
        return res.data;
    },
};

// 학원 - 트로피/뱃지 API
export const academyTrophyBadgeApi = {
    /**
     * 트로피/뱃지 마스터 데이터 목록 조회
     * @param {{ type?: string }} params
     */
    listMaster: async (params = {}) => {
        const { type } = params;
        const res = await api.get('/academy/trophy-badges', { params: { type } });
        return res.data;
    },
    /**
     * 트로피/뱃지 마스터 상세 조회
     * @param {number} id
     */
    getMasterDetail: async (id) => {
        const res = await api.get(`/academy/trophy-badges/${id}`);
        return res.data;
    },
    /**
     * 트로피/뱃지 수여 목록 조회 (해당 학원의 학생만)
     * @param {{ academyCode: string, type?: string, page?: number, size?: number }} params
     */
    listAwards: async (params = {}) => {
        const { academyCode, type, page = 0, size = 10 } = params;
        const res = await api.get('/academy/trophy-badges/awards', { params: { academyCode, type, page, size } });
        return res.data;
    },
    /**
     * 트로피/뱃지 수여 상세 조회
     * @param {number} id
     * @param {string} academyCode
     */
    getAwardDetail: async (id, academyCode) => {
        const res = await api.get(`/academy/trophy-badges/awards/${id}`, { params: { academyCode } });
        return res.data;
    },
    /**
     * 트로피/뱃지 수여 정보 수정(학원)
     * @param {number} id
     * @param {{ trophyBadgeId?: number, studentUserId?: number, reason?: string, awardedAt?: string }} payload
     */
    updateAward: async (id, payload) => {
        const res = await api.put(`/academy/trophy-badges/awards/${id}`, payload);
        return res.data;
    },
    /**
     * 트로피/뱃지 수여
     * @param {{ academyCode: string, trophyBadgeId: number, studentUserIds: number[], reason?: string, awardedAt?: string }} payload
     * @note grantedBy는 백엔드에서 현재 로그인한 관리자로 자동 설정됩니다.
     */
    createAward: async (payload) => {
        const res = await api.post('/academy/trophy-badges/awards', payload);
        return res.data;
    },
};

// 시험 결과 관리 API
export const adminExamResultApi = {
    /**
     * 시험 결과 목록 조회
     * @param {{ examName?: string, academyCode?: string, studentName?: string, gradingStatus?: string }} params
     */
    list: async (params = {}) => {
        const res = await api.get('/admin/exam-results', { params });
        return res.data;
    },
    /**
     * 시험 결과 상세 조회
     * @param {number} id
     */
    detail: async (id) => {
        const res = await api.get(`/admin/exam-results/${id}`);
        return res.data;
    },
    /**
     * 채점을 위한 시험 결과 상세 조회
     * @param {number} id
     */
    gradingDetail: async (id) => {
        const res = await api.get(`/admin/exam-results/${id}/grading`);
        return res.data;
    },
    /**
     * 문항별 채점 저장
     * @param {number} id - ExamResult ID
     * @param {{ resultItemId: number, isCorrect: boolean, score: number }} data
     */
    gradeItem: async (id, data) => {
        const res = await api.post(`/admin/exam-results/${id}/grade-item`, data);
        return res.data;
    },
    /**
     * 채점 완료 처리
     * @param {number} id - ExamResult ID
     * @param {{ totalScore: number, correctCount: number, wrongCount: number, guideline?: string }} data
     */
    completeGrading: async (id, data) => {
        const res = await api.post(`/admin/exam-results/${id}/complete-grading`, data);
        return res.data;
    },
    /**
     * 재채점 시작 (채점 완료 → 진행중으로 변경)
     * @param {number} id - ExamResult ID
     */
    restartGrading: async (id) => {
        const res = await api.post(`/admin/exam-results/${id}/restart-grading`);
        return res.data;
    },
    /**
     * AI 코멘트 생성
     * @param {number} id - ExamResult ID
     */
    aiGenerateComments: async (id) => {
        const res = await api.post(`/admin/exam-results/${id}/ai-comments`);
        return res.data;
    },
};

// 학원 - 시험 결과 상세 API
export const academyExamResultApi = {
    /**
     * 학원용 시험 결과 상세 조회 (관리자 상세와 동일 포맷)
     * @param {number} id - examResultId
     */
    detail: async (id) => {
        const res = await api.get(`/academy/exam-results/${id}/detail`);
        return res.data;
    },
};

// 학생 마이페이지 API
export const studentMypageApi = {
    /**
     * 학생 마이페이지 정보 조회
     */
    getMyInfo: async () => {
        const res = await api.get('/student/mypage/info');
        return res.data;
    },
    /**
     * 학생의 트로피/뱃지 목록 조회
     */
    getTrophyBadgeList: async () => {
        const res = await api.get('/student/mypage/trophy-badge/list');
        return res.data;
    },
    /**
     * 비밀번호 변경
     */
    changePassword: async ({ currentPassword, newPassword, confirmPassword }) => {
        const res = await api.post('/student/mypage/change-password', {
            currentPassword,
            newPassword,
            confirmPassword
        });
        return res.data;
    },
};

/**
 * 학생 대시보드 API
 */
export const studentDashboardApi = {
    /**
     * 학생 대시보드 정보 조회 (최근 학습, 최근 숙제, 오늘의 단어, 최근 뱃지)
     */
    getDashboardInfo: async () => {
        const res = await api.get('/student/dashboard/info');
        return res.data;
    },
};

// Default export for direct axios usage (e.g., public APIs)
export default api;